import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Search, Plus, X, Camera, MapPin, Tag, DollarSign, ArrowLeftRight,
  MessageCircle, Mail, ChevronLeft, Trash2, Edit, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const CLUB_TYPES = ['Driver', 'Irons', 'Wedge', 'Putter', 'Hybrid', 'Fairway Wood', 'Bag', 'Accessories', 'Other'];
const CONDITIONS = ['New', 'Excellent', 'Good', 'Fair'];

export default function GearExchange() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Create / Edit
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [form, setForm] = useState(getEmptyForm());

  // Detail view
  const [selectedListing, setSelectedListing] = useState(null);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  // Photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) fetchListings();
  }, [user, filterType, filterCondition]);

  function getEmptyForm() {
    return { title: '', brand: '', club_type: 'Driver', condition: 'Good', price: '', trade_option: false, trade_description: '', location: '', photos: [], description: '' };
  }

  const fetchListings = async () => {
    setLoadingListings(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('club_type', filterType);
      if (filterCondition) params.append('condition', filterCondition);
      const res = await fetch(`${API}/gear?${params}`, { credentials: 'include' });
      if (res.ok) setListings(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingListings(false); }
  };

  const searchListings = async () => {
    if (!search.trim()) { fetchListings(); return; }
    setLoadingListings(true);
    try {
      const params = new URLSearchParams({ search });
      if (filterType) params.append('club_type', filterType);
      if (filterCondition) params.append('condition', filterCondition);
      const res = await fetch(`${API}/gear?${params}`, { credentials: 'include' });
      if (res.ok) setListings(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingListings(false); }
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      // Compress image client-side before uploading
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const res = await fetch(`${API}/gear/upload-photo`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) {
        const { photo_url } = await res.json();
        setForm(prev => ({ ...prev, photos: [...prev.photos, photo_url] }));
        toast.success('Photo added');
      } else { toast.error('Upload failed'); }
    } catch { toast.error('Upload failed'); }
    finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePhoto = (idx) => {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
  };

  const submitListing = async () => {
    if (!form.title.trim() || !form.brand.trim()) { toast.error('Title and Brand are required'); return; }
    const body = {
      ...form,
      price: form.price ? parseFloat(form.price) : null,
    };
    try {
      const url = editingListing ? `${API}/gear/${editingListing.listing_id}` : `${API}/gear`;
      const method = editingListing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(editingListing ? 'Listing updated!' : 'Listing created!');
        setShowForm(false);
        setEditingListing(null);
        setForm(getEmptyForm());
        fetchListings();
      } else { toast.error('Failed to save listing'); }
    } catch { toast.error('Failed to save listing'); }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      const res = await fetch(`${API}/gear/${listingId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success('Listing deleted');
        setSelectedListing(null);
        fetchListings();
      }
    } catch { toast.error('Failed to delete'); }
  };

  const markSold = async (listingId) => {
    try {
      await fetch(`${API}/gear/${listingId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'sold' }),
      });
      toast.success('Marked as sold!');
      setSelectedListing(null);
      fetchListings();
    } catch { toast.error('Failed to update'); }
  };

  const openEdit = (listing) => {
    setEditingListing(listing);
    setForm({
      title: listing.title, brand: listing.brand, club_type: listing.club_type,
      condition: listing.condition, price: listing.price || '', trade_option: listing.trade_option,
      trade_description: listing.trade_description || '', location: listing.location || '',
      photos: listing.photos || [], description: listing.description || '',
    });
    setShowForm(true);
    setSelectedListing(null);
  };

  const messageSeller = (listing) => {
    navigate('/chat');
    // DM will be initiated from chat page
  };

  const emailSeller = (listing) => {
    const subject = encodeURIComponent(`Bundle Gear Exchange: ${listing.title}`);
    const body = encodeURIComponent(`Hi ${listing.seller_name},\n\nI'm interested in your listing "${listing.title}" on Bundle Gear Exchange.\n\nLet me know if it's still available!\n\nThanks`);
    window.open(`mailto:${listing.seller_email}?subject=${subject}&body=${body}`, '_self');
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-bundle-cream"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green" /></div>;
  }

  // ============ DETAIL VIEW ============
  if (selectedListing) {
    const l = selectedListing;
    const isOwner = l.seller_id === user.user_id;
    return (
      <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
        <Navigation user={user} onLogout={logout} />
        <div className="max-w-2xl mx-auto px-4 pt-20 md:pt-24">
          <button onClick={() => setSelectedListing(null)} className="flex items-center gap-1 text-bundle-green font-body text-sm mb-4 hover:underline" data-testid="back-to-listings">
            <ChevronLeft className="w-4 h-4" /> Back to listings
          </button>

          {/* Photos */}
          {l.photos?.length > 0 ? (
            <div className="flex gap-2 overflow-x-auto pb-3 mb-4 snap-x">
              {l.photos.map((p, i) => (
                <div key={i} className="w-72 h-56 bg-gray-50 rounded-xl flex-shrink-0 snap-start border border-gray-200 flex items-center justify-center">
                  <img src={p.startsWith('/api') ? `${window.location.origin}${p}` : p} alt="" className="max-w-full max-h-56 object-contain" />
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
              <Tag className="w-12 h-12 text-gray-300" />
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-4">
            {/* Photo gallery */}
            {l.photos?.length > 0 && (
              <div className="relative bg-gray-50 flex items-center justify-center">
                <img
                  src={l.photos[activePhotoIdx || 0]?.startsWith('/api') ? `${window.location.origin}${l.photos[activePhotoIdx || 0]}` : l.photos[activePhotoIdx || 0]}
                  alt={l.title}
                  data-testid="listing-detail-photo"
                  className="max-w-full max-h-80 md:max-h-96 object-contain"
                />
                {l.photos.length > 1 && (
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                    {l.photos.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActivePhotoIdx(i)}
                        className={`w-2 h-2 rounded-full transition-all ${i === (activePhotoIdx || 0) ? 'bg-white w-4' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="p-5">
            <div className="flex items-start justify-between mb-2">
              <h1 className="font-heading font-bold text-xl text-bundle-green">{l.title}</h1>
              {l.status === 'sold' && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">SOLD</span>}
            </div>
            <p className="font-body text-sm text-gray-500 mb-3">{l.brand} &middot; {l.club_type} &middot; {l.condition}</p>

            <div className="flex items-center gap-3 mb-4">
              {l.price && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-bundle-green text-white font-heading font-bold text-lg rounded-lg">
                  <DollarSign className="w-4 h-4" />{l.price}
                </span>
              )}
              {l.trade_option && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-bundle-gold/20 text-bundle-green font-body text-sm font-semibold rounded-lg">
                  <ArrowLeftRight className="w-4 h-4" /> Open to Trade
                </span>
              )}
            </div>

            {l.trade_description && (
              <p className="font-body text-sm text-gray-600 mb-3"><span className="font-semibold">Trade for:</span> {l.trade_description}</p>
            )}

            {l.location && (
              <p className="font-body text-sm text-gray-500 flex items-center gap-1 mb-3"><MapPin className="w-4 h-4" /> {l.location}</p>
            )}

            {l.description && (
              <p className="font-body text-sm text-gray-700 leading-relaxed mb-4">{l.description}</p>
            )}

            <p className="font-body text-xs text-gray-400">Listed by {l.seller_name} &middot; {new Date(l.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Actions */}
          {isOwner ? (
            <div className="flex gap-2">
              <Button onClick={() => openEdit(l)} className="flex-1 btn-bundle" data-testid="edit-listing-btn"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
              <Button onClick={() => markSold(l.listing_id)} variant="outline" className="flex-1 border-bundle-gold text-bundle-green" data-testid="mark-sold-btn">Mark as Sold</Button>
              <Button onClick={() => deleteListing(l.listing_id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-listing-btn"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ) : l.status === 'active' ? (
            <div className="flex gap-2">
              <Button onClick={() => messageSeller(l)} className="flex-1 btn-bundle" data-testid="dm-seller-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Message Seller
              </Button>
              {l.seller_email && (
                <Button onClick={() => emailSeller(l)} variant="outline" className="flex-1 border-bundle-green text-bundle-green" data-testid="email-seller-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email Seller
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ============ MAIN LISTINGS VIEW ============
  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-5xl mx-auto px-4 pt-20 md:pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-bundle-green">Bundle Marketplace</h1>
            <p className="font-body text-sm text-gray-500 mt-1">Buy, sell, or trade equipment with members</p>
          </div>
          <Button data-testid="create-listing-btn" onClick={() => { setEditingListing(null); setForm(getEmptyForm()); setShowForm(true); }} className="btn-bundle">
            <Plus className="w-4 h-4 mr-1" /> List Item
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="flex gap-2 mb-4">
          <form onSubmit={(e) => { e.preventDefault(); searchListings(); }} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="gear-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search gear..."
              className="pl-9 border-gray-200 focus:border-bundle-green text-sm"
            />
          </form>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={`border-gray-200 ${showFilters || filterType || filterCondition ? 'text-bundle-green border-bundle-green' : 'text-gray-500'}`}>
            <Filter className="w-4 h-4" />
          </Button>
        </div>

        {showFilters && (
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="gear-filters">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-9 px-3 border border-gray-200 rounded-md text-sm focus:border-bundle-green focus:outline-none bg-white">
              <option value="">All Types</option>
              {CLUB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterCondition} onChange={(e) => setFilterCondition(e.target.value)} className="h-9 px-3 border border-gray-200 rounded-md text-sm focus:border-bundle-green focus:outline-none bg-white">
              <option value="">All Conditions</option>
              {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(filterType || filterCondition) && (
              <button onClick={() => { setFilterType(''); setFilterCondition(''); }} className="text-xs text-red-500 hover:underline font-body">Clear</button>
            )}
          </div>
        )}

        {/* Listings Grid */}
        {loadingListings ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Tag className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <h3 className="font-heading font-bold text-lg text-gray-500 mb-1">No listings yet</h3>
            <p className="font-body text-sm text-gray-400">Be the first to list your gear!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gear-listings-grid">
            {listings.map((l) => (
              <button
                key={l.listing_id}
                data-testid={`gear-card-${l.listing_id}`}
                onClick={() => { setActivePhotoIdx(0); setSelectedListing(l); }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-left group"
              >
                {l.photos?.length > 0 ? (
                  <div className="w-full h-48 bg-gray-50 flex items-center justify-center">
                    <img src={l.photos[0].startsWith('/api') ? `${window.location.origin}${l.photos[0]}` : l.photos[0]} alt="" className="max-w-full max-h-48 object-contain" />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <Tag className="w-8 h-8 text-gray-300" />
                  </div>
                )}
                <div className="p-3">
                  <h3 className="font-body font-semibold text-sm text-gray-900 truncate group-hover:text-bundle-green transition-colors">{l.title}</h3>
                  <p className="font-body text-xs text-gray-500 truncate">{l.brand} &middot; {l.condition}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {l.price && <span className="font-heading font-bold text-sm text-bundle-green">${l.price}</span>}
                    {l.trade_option && <span className="text-[10px] px-1.5 py-0.5 bg-bundle-gold/20 text-bundle-green rounded-full font-body font-semibold">Trade</span>}
                  </div>
                  {l.status === 'sold' && <span className="mt-1 inline-block px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">SOLD</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-10 mb-6 text-center">
          <p className="font-body text-xs text-gray-400 max-w-lg mx-auto leading-relaxed">
            Bundle Golf Club only provides a platform for members to connect. All transactions are the responsibility of the buyers and sellers. Any unethical trading practices or complaints may be the subject of membership being revoked.
          </p>
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-bundle-green">{editingListing ? 'Edit Listing' : 'List Your Gear'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Item Title *</label>
              <Input data-testid="gear-title-input" value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. TaylorMade Stealth 2 Driver" className="border-gray-200 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Brand *</label>
                <Input data-testid="gear-brand-input" value={form.brand} onChange={(e) => setForm(p => ({ ...p, brand: e.target.value }))} placeholder="e.g. TaylorMade" className="border-gray-200 text-sm" />
              </div>
              <div>
                <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Club Type</label>
                <select data-testid="gear-type-select" value={form.club_type} onChange={(e) => setForm(p => ({ ...p, club_type: e.target.value }))} className="w-full h-9 px-3 border border-gray-200 rounded-md text-sm focus:border-bundle-green focus:outline-none">
                  {CLUB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Condition</label>
                <select data-testid="gear-condition-select" value={form.condition} onChange={(e) => setForm(p => ({ ...p, condition: e.target.value }))} className="w-full h-9 px-3 border border-gray-200 rounded-md text-sm focus:border-bundle-green focus:outline-none">
                  {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Price ($)</label>
                <Input data-testid="gear-price-input" type="number" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} placeholder="0.00" className="border-gray-200 text-sm" />
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.trade_option} onChange={(e) => setForm(p => ({ ...p, trade_option: e.target.checked }))} className="rounded border-gray-300 text-bundle-green focus:ring-bundle-green" />
                <span className="font-body text-sm text-gray-700">Open to trade</span>
              </label>
              {form.trade_option && (
                <Input data-testid="gear-trade-input" value={form.trade_description} onChange={(e) => setForm(p => ({ ...p, trade_description: e.target.value }))} placeholder="What would you trade for?" className="border-gray-200 text-sm mt-2" />
              )}
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Location</label>
              <Input data-testid="gear-location-input" value={form.location} onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Atlanta, GA" className="border-gray-200 text-sm" />
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Description</label>
              <textarea data-testid="gear-desc-input" value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Describe the item, shaft specs, loft, etc." className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-bundle-green focus:outline-none resize-none" />
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Photos</label>
              <div className="flex gap-2 flex-wrap">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                    <img src={p.startsWith('/api') ? `${window.location.origin}${p}` : p} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <button
                    type="button"
                    data-testid="gear-add-photo-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-bundle-green hover:text-bundle-green transition-colors"
                  >
                    {uploadingPhoto ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-bundle-green" /> : <><Camera className="w-5 h-5" /><span className="text-[10px] mt-0.5">Add</span></>}
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </div>
            <Button data-testid="submit-listing-btn" onClick={submitListing} className="w-full btn-bundle py-5" disabled={!form.title.trim() || !form.brand.trim()}>
              {editingListing ? 'Update Listing' : 'Post Listing'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
